import React, { useState, useEffect } from 'react';
import { Calculator, Save, Lock, Calendar, FileText, Settings as SettingsIcon } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const MONTHS = [
  { v: 1, l: 'January' }, { v: 2, l: 'February' }, { v: 3, l: 'March' },
  { v: 4, l: 'April' }, { v: 5, l: 'May' }, { v: 6, l: 'June' },
  { v: 7, l: 'July' }, { v: 8, l: 'August' }, { v: 9, l: 'September' },
  { v: 10, l: 'October' }, { v: 11, l: 'November' }, { v: 12, l: 'December' },
];

const CCYS = ['SGD', 'USD', 'EUR', 'GBP', 'JPY', 'HKD', 'THB', 'IDR', 'MVR', 'AUD'];

const COA_TEMPLATES = [
  { v: 'sg_standard', l: 'Singapore standard CoA' },
  { v: 'sg_smes', l: 'SG SMEs (FRS for Small Entities)' },
  { v: 'us_gaap', l: 'US GAAP basic' },
  { v: 'custom', l: 'Custom (none)' },
];

const STATEMENT_FORMATS = [
  { v: '', l: '— Not configured —' },
  { v: 'dbs', l: 'DBS' },
  { v: 'ocbc', l: 'OCBC' },
  { v: 'uob', l: 'UOB' },
  { v: 'wise', l: 'Wise' },
  { v: 'revolut', l: 'Revolut' },
  { v: 'chase', l: 'Chase (US)' },
  { v: 'generic_csv', l: 'Generic CSV' },
];

export default function LedgerSettings({ business }) {
  const [s, setS] = useState(business.ledger_settings || {});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Subsections collapsed/expanded
  const update = (patch) => setS((prev) => ({ ...prev, ...patch }));
  const updateNested = (key, patch) => setS((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  const save = async () => {
    setSaving(true);
    await updateDoc(doc(db, 'businesses', business.id), { ledger_settings: s });
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 1500);
  };

  return (
    <div className="settings-panel" style={{ marginTop: 16 }}>
      <div className="settings-header">
        <Calculator size={20} />
        <h3>Ledger Settings</h3>
      </div>

      {/* Fiscal year + currency */}
      <div className="settings-section">
        <h4 className="settings-section-title"><Calendar size={14} /> Reporting period</h4>
        <div className="settings-field">
          <label>Fiscal year start month</label>
          <select value={s.fiscal_year_start_month || 1} onChange={(e) => update({ fiscal_year_start_month: Number(e.target.value) })}>
            {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
        </div>
        <div className="settings-field">
          <label>Functional currency</label>
          <select value={s.functional_currency || (business.currencies?.[0] || 'SGD')} onChange={(e) => update({ functional_currency: e.target.value })}>
            {CCYS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* GST/Tax */}
      <div className="settings-section">
        <h4 className="settings-section-title">Tax (GST/VAT)</h4>
        <label className="settings-check-row">
          <input type="checkbox" checked={s.gst?.registered || false} onChange={(e) => updateNested('gst', { registered: e.target.checked })} />
          <span>Tax-registered</span>
        </label>
        {s.gst?.registered && (
          <>
            <div className="settings-field">
              <label>Tax rate (%)</label>
              <input type="number" step="0.01" value={s.gst?.rate || 9} onChange={(e) => updateNested('gst', { rate: Number(e.target.value) })} />
            </div>
            <div className="settings-field">
              <label>Registration date</label>
              <input type="date" value={s.gst?.registration_date || ''} onChange={(e) => updateNested('gst', { registration_date: e.target.value || null })} />
            </div>
            <div className="settings-field">
              <label>Tax registration number</label>
              <input value={s.gst?.registration_number || ''} onChange={(e) => updateNested('gst', { registration_number: e.target.value })} placeholder="e.g. M2-1234567-8" />
            </div>
          </>
        )}
      </div>

      {/* Chart of accounts */}
      <div className="settings-section">
        <h4 className="settings-section-title">Chart of Accounts</h4>
        <div className="settings-field">
          <label>Template</label>
          <select value={s.chart_of_accounts_template || 'custom'} onChange={(e) => update({ chart_of_accounts_template: e.target.value })}>
            {COA_TEMPLATES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </div>
        <p className="settings-hint">CoA editor lives in the Ledger tab (recon_items). Template seeds standard accounts on first use.</p>
      </div>

      {/* Period locking */}
      <div className="settings-section">
        <h4 className="settings-section-title"><Lock size={14} /> Period lock</h4>
        <p className="settings-hint">No back-dated entries before this date (owner can override).</p>
        <div className="settings-field">
          <label>Locked through</label>
          <input type="date" value={s.period_lock?.locked_until || ''} onChange={(e) => updateNested('period_lock', { locked_until: e.target.value || null })} />
        </div>
        <label className="settings-check-row">
          <input type="checkbox" checked={s.period_lock?.allow_owner_override || true} onChange={(e) => updateNested('period_lock', { allow_owner_override: e.target.checked })} />
          <span>Allow owner override</span>
        </label>
      </div>

      {/* Auto-match (recon) */}
      <div className="settings-section">
        <h4 className="settings-section-title">Reconciliation auto-match</h4>
        <div className="settings-field">
          <label>Date tolerance (days)</label>
          <input type="number" value={s.auto_match?.date_window_days ?? 3} onChange={(e) => updateNested('auto_match', { date_window_days: Number(e.target.value) })} />
        </div>
        <div className="settings-field">
          <label>Amount tolerance (%)</label>
          <input type="number" step="0.01" value={s.auto_match?.amount_tolerance_pct ?? 0.01} onChange={(e) => updateNested('auto_match', { amount_tolerance_pct: Number(e.target.value) })} />
        </div>
        <label className="settings-check-row">
          <input type="checkbox" checked={s.auto_match?.require_review_above_amount > 0} onChange={(e) => updateNested('auto_match', { require_review_above_amount: e.target.checked ? 5000 : 0 })} />
          <span>Require manual review above SGD 5000</span>
        </label>
      </div>

      {/* Bank statement formats */}
      <div className="settings-section">
        <h4 className="settings-section-title">Bank statement formats</h4>
        <p className="settings-hint">Template per bank account (used by CSV import).</p>
        <BankFormatList business={business} settings={s} updateNested={updateNested} />
      </div>

      {/* Bank ↔ CoA */}
      <div className="settings-section">
        <h4 className="settings-section-title">Bank → Chart of Accounts mapping</h4>
        <p className="settings-hint">Map each bank account to a CoA code (e.g. DBS → 1100). Editor coming when CoA list is wired — for now stored as JSON.</p>
        <textarea
          rows={3}
          value={JSON.stringify(s.bank_to_coa_mapping || {}, null, 2)}
          onChange={(e) => {
            try { update({ bank_to_coa_mapping: JSON.parse(e.target.value) }); } catch { /* ignore parse */ }
          }}
          style={{ width: '100%', padding: 8, fontFamily: 'monospace', fontSize: 12 }}
        />
      </div>

      {/* Recurring journals */}
      <div className="settings-section">
        <h4 className="settings-section-title">Recurring journals</h4>
        <p className="settings-hint">Auto-post on schedule (rent, payroll, depreciation). Full editor coming — for now a list of templates.</p>
        <RecurringJournalList settings={s} update={update} />
      </div>

      {/* Approval workflow */}
      <div className="settings-section">
        <h4 className="settings-section-title">Approval workflow</h4>
        <div className="settings-field">
          <label>Mode</label>
          <select value={s.approval_workflow || 'single_entry'} onChange={(e) => update({ approval_workflow: e.target.value })}>
            <option value="single_entry">Single entry — post immediately</option>
            <option value="draft_approve_post">Draft → approve → post</option>
            <option value="draft_approve_post_with_reviewer">Draft → reviewer → approve → post</option>
          </select>
        </div>
      </div>

      {/* Default expense categories */}
      <div className="settings-section">
        <h4 className="settings-section-title">Default expense categories</h4>
        <p className="settings-hint">Comma-separated. Used as quick-pick on new expense entries.</p>
        <textarea
          rows={2}
          value={(s.default_expense_categories || []).join(', ')}
          onChange={(e) => update({ default_expense_categories: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
          style={{ width: '100%', padding: 8, fontSize: 13 }}
          placeholder="Rent, Utilities, Salaries, Software, Travel"
        />
      </div>

      {/* Retention */}
      <div className="settings-section">
        <h4 className="settings-section-title"><FileText size={14} /> Retention</h4>
        <div className="settings-field">
          <label>Document retention (months)</label>
          <input type="number" value={s.document_retention_months ?? 84} onChange={(e) => update({ document_retention_months: Number(e.target.value) })} />
          <p className="settings-hint" style={{ marginTop: 4 }}>Default 84 months (7 years — IRAS minimum)</p>
        </div>
        <div className="settings-field">
          <label>Audit log retention (months)</label>
          <input type="number" value={s.audit_log_retention_months ?? 84} onChange={(e) => update({ audit_log_retention_months: Number(e.target.value) })} />
        </div>
      </div>

      {/* Save */}
      <div style={{ position: 'sticky', bottom: 0, background: 'white', padding: '12px 0', display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #eee', marginTop: 16 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving…' : (saved ? 'Saved ✓' : 'Save Ledger Settings')}
        </button>
      </div>
      <p className="settings-hint" style={{ marginTop: 6 }}>
        Note: most settings are stored but not yet enforced. Period locking, auto-match, recurring journals, retention purges, approval workflow — enforcement wiring in follow-up work.
      </p>
    </div>
  );
}

// ── Bank statement format list ──

function BankFormatList({ business, settings, updateNested }) {
  // Bank accounts come from recon_accounts subcollection (loaded async)
  const [bankAccounts, setBankAccounts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { collection, getDocs } = await import('firebase/firestore');
        const snap = await getDocs(collection(db, 'businesses', business.id, 'recon_accounts'));
        if (!cancelled) setBankAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [business.id]);

  if (bankAccounts.length === 0) {
    return <p style={{ color: '#888', fontSize: 13 }}>No bank accounts in this business yet (add via Ledger tab → recon).</p>;
  }

  const formats = settings.bank_statement_formats || {};

  return (
    <table style={{ width: '100%', fontSize: 13 }}>
      <thead><tr style={{ color: '#888' }}><th style={{ textAlign: 'left' }}>Bank account</th><th style={{ textAlign: 'left' }}>Format</th></tr></thead>
      <tbody>
        {bankAccounts.map((acc) => (
          <tr key={acc.id}>
            <td style={{ padding: '4px 0' }}>{acc.name || acc.id}</td>
            <td>
              <select
                value={formats[acc.id] || ''}
                onChange={(e) => updateNested('bank_statement_formats', { ...formats, [acc.id]: e.target.value })}
                style={{ padding: 4, fontSize: 13 }}
              >
                {STATEMENT_FORMATS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
              </select>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Recurring journals list ──

function RecurringJournalList({ settings, update }) {
  const list = settings.recurring_journals || [];
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [freq, setFreq] = useState('monthly');

  const add = () => {
    if (!name.trim() || !amount) return;
    const next = [...list, { id: Date.now().toString(), name: name.trim(), amount: Number(amount), frequency: freq, created_at: new Date().toISOString() }];
    update({ recurring_journals: next });
    setName(''); setAmount('');
  };

  const remove = (id) => {
    update({ recurring_journals: list.filter((j) => j.id !== id) });
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <input placeholder="Name (e.g. Rent)" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1, padding: 6, fontSize: 13 }} />
        <input placeholder="Amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ width: 100, padding: 6, fontSize: 13 }} />
        <select value={freq} onChange={(e) => setFreq(e.target.value)} style={{ padding: 6, fontSize: 13 }}>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="annually">Annually</option>
        </select>
        <button className="btn btn-primary" onClick={add}>+</button>
      </div>
      {list.length === 0 ? (
        <p style={{ color: '#888', fontSize: 13 }}>No recurring journals yet.</p>
      ) : (
        <table style={{ width: '100%', fontSize: 13 }}>
          <thead><tr style={{ color: '#888' }}><th style={{ textAlign: 'left' }}>Name</th><th style={{ textAlign: 'right' }}>Amount</th><th>Frequency</th><th></th></tr></thead>
          <tbody>{list.map((j) => (
            <tr key={j.id}>
              <td style={{ padding: '4px 0' }}>{j.name}</td>
              <td style={{ textAlign: 'right' }}>{Number(j.amount).toLocaleString()}</td>
              <td>{j.frequency}</td>
              <td><button className="btn btn-sm btn-danger" onClick={() => remove(j.id)}>×</button></td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </>
  );
}
